import { useEffect, useState } from 'react';

import { debounce } from '@ce-cfbdigital/cgl-cfb-fe-foundation';
import PropTypes from 'prop-types';
import PubSub from 'pubsub-js';

import { NOTIFY_TYPES } from '../constants';
import CustomIcon from '../CustomIcons';

import './styles/index.css';

/**
 * A component to display snackbar messages
 */

const bgColorProgress = {
  alert: '#a40001',
  caution: '#ca8e00',
  success: '#466d0e'
};

const SnackbarWrapper = ({ autoClose }) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [scrollTop, setScrollToTop] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [progress, setProgress] = useState(100);
  const [autoCloseBanner, setAutoCloseBanner] = useState(autoClose);
  const [type, setType] = useState('');

  let autoCloseSnackBar = null;

  const getIconByType = iconType => {
    const iconMap = {
      alert: <CustomIcon size="26" type="info" className="error-icon" color="#CD0D15" />,
      caution: <CustomIcon size="26" type="warning" className="warning-icon" color="#FFB100" />,
      success: <CustomIcon size="26" type="check" className="info-icon" color="#019F4A" />
    };
    return iconMap[iconType] || null;
  };

  const handleScrollToTop = () => {
    if (showToast) setScrollToTop(window.scrollY + 64);
  };

  const handleClose = () => {
    setShowToast(false);
    clearTimeout(autoCloseSnackBar);
  };

  /**
   * updateError - updates error message
   * @param {object} error contains error message/text, code and other error info
   * @description type of errors: SUCCESS(green),CAUTION(yellow),ALERT(red). Error may come with: notifyType: 'success'||'caution'||'alert'
   */
  const updateError = (msg, error) => {
    if (!error) return;
    const { notifyType = NOTIFY_TYPES.ALERT } = error;
    const receivedErrorMsg = error && (error.text || error.message);

    const autoCloseTimer =
      typeof receivedErrorMsg === 'string' && receivedErrorMsg.length > 100 ? autoClose * 2 : autoClose;
    setAutoCloseBanner(autoCloseTimer);

    if (!showToast && receivedErrorMsg) {
      setErrorMessage(receivedErrorMsg);
      setType(notifyType);
      setScrollToTop(window.scrollY + 64);
      setShowToast(true);
      autoCloseSnackBar = setTimeout(handleClose, autoCloseTimer);
    }
  };

  /**
   * subscribes pubsub with updateError method
   * @description If you want to send a custom error instead of defaultErrors you must unsuscribe from xhrError and suscribe to customErrorMessage
   */
  useEffect(() => {
    window.addEventListener('scroll', handleScrollToTop);

    let interval = '';
    if (showToast) {
      interval = setInterval(() => {
        setProgress(prevProgress => Math.max(prevProgress - 100 / (autoCloseBanner / 100), 0));
      }, 100);
      setProgress(100);
    }
    PubSub.subscribe('xhrError', debounce(updateError, 500));
    PubSub.subscribe('customErrorMessage', debounce(updateError, 500));

    return () => {
      window.removeEventListener('scroll', handleScrollToTop);
      PubSub.unsubscribe('xhrError');
      PubSub.unsubscribe('customErrorMessage');
      clearInterval(interval);
    };
  }, [showToast]);

  /**
   * @method render
   * @description Renders the ui
   * @returns {*} Rendered component
   */
  return (
    <div className={`snackbar ${type} ${showToast ? 'show' : 'close'}`} style={{ top: scrollTop }}>
      <div className="snackbar__content">
        <div className="snackbar__details">
          {getIconByType(type)}
          <span>{errorMessage}</span>
        </div>
        <CustomIcon className="snackbar__btn-close" type="close" size="22" onClick={handleClose} />
      </div>
      <div className="snackbar__progress" style={{ width: `${progress}%`, backgroundColor: bgColorProgress[type] }} />
    </div>
  );
};

SnackbarWrapper.propTypes = {
  autoClose: PropTypes.number.isRequired
};

export default SnackbarWrapper;
